import * as PopoverPrimitive from '@radix-ui/react-popover'
import { GalleryHorizontalEndIcon, ImageIcon, PlayIcon } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { ETIQUETA_TIPO, type TipoPublicacion } from '@/lib/schemas/publicaciones'

const ICONO: Record<TipoPublicacion, typeof ImageIcon> = {
  imagen: ImageIcon,
  carrusel: GalleryHorizontalEndIcon,
  video: PlayIcon,
}

interface IconoTipoProps {
  tipo: TipoPublicacion
  /** Clases del contenedor del ícono (fondo, tamaño, posición). */
  className?: string
  /** Clases del ícono en sí. */
  claseIcono?: string
  /**
   * Qué hace un clic con el mouse. En el calendario el ícono está dentro del
   * botón de la pieza y el clic le llega solo; en la grilla está encima de la
   * portada y hay que pasárselo.
   */
  alHacerClic?: () => void
}

/**
 * Ícono de tipo de pieza con su nombre en un cartelito (revisión OO del
 * 23/09/2026: "algo simple que ayude al aprendizaje natural"). Con el mouse
 * aparece al pasar por encima; en el celular, al tocar el ícono, sin abrir la
 * pieza, y se va solo a los dos segundos o al tocar en otro lado.
 *
 * El cartel va en un portal: las celdas del calendario recortan lo que se sale.
 */
export function IconoTipo({ tipo, className, claseIcono, alHacerClic }: IconoTipoProps) {
  const [abierto, setAbierto] = useState(false)
  const puntero = useRef<string>('mouse')
  const Icono = ICONO[tipo]

  useEffect(() => {
    if (!abierto || puntero.current === 'mouse') return
    const t = window.setTimeout(() => {
      setAbierto(false)
    }, 2000)
    return () => {
      window.clearTimeout(t)
    }
  }, [abierto])

  return (
    <PopoverPrimitive.Root open={abierto} onOpenChange={setAbierto}>
      <PopoverPrimitive.Anchor asChild>
        <span
          role="img"
          aria-label={ETIQUETA_TIPO[tipo]}
          data-icono-tipo={tipo}
          className={className}
          onPointerEnter={(e) => {
            puntero.current = e.pointerType
            if (e.pointerType === 'mouse') setAbierto(true)
          }}
          onPointerLeave={(e) => {
            if (e.pointerType === 'mouse') setAbierto(false)
          }}
          onPointerDown={(e) => {
            puntero.current = e.pointerType
          }}
          onClick={(e) => {
            if (puntero.current === 'mouse') {
              setAbierto(false)
              alHacerClic?.()
              return
            }
            // Tocar el ícono muestra el nombre; la pieza se abre tocando la portada.
            e.stopPropagation()
            e.preventDefault()
            setAbierto((a) => !a)
          }}
        >
          <Icono className={claseIcono} aria-hidden />
        </span>
      </PopoverPrimitive.Anchor>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          role="tooltip"
          data-cartel-tipo
          side="top"
          sideOffset={6}
          collisionPadding={8}
          onOpenAutoFocus={(e) => {
            e.preventDefault()
          }}
          className="pointer-events-none z-[60] rounded-lg bg-tinta px-2.5 py-1 text-xs font-medium text-white shadow-tarjeta"
        >
          {ETIQUETA_TIPO[tipo]}
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  )
}
